import { Button, Checkbox, ColorPicker, InputNumber, message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { cloudFrontUrl, serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const DEFAULT_SETTINGS = {
  show_logo: true,
  show_school_name: true,
  show_address: true,
  show_phone: true,
  show_email: true,
  show_website: false,
  accent_color: "#1a237e",
  name_font_size: 16,
  text_color: "#4B5563",
  text_font_size: 9,
  logo_size: 60,
};

function PdfHeaderSettings() {
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);
  const schoolId = user?.school?._id;
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);

  async function fetchSchool() {
    const res = await axios.get(`${serverUrl}/school/${schoolId}`, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const schoolQuery = useQuery(["school", schoolId], fetchSchool, {
    enabled: !!schoolId,
  });
  const school = schoolQuery.data?.result?.data;

  useEffect(() => {
    if (school) {
      setSettings({ ...DEFAULT_SETTINGS, ...(school.pdf_header_settings || {}) });
    }
  }, [school]);

  function updateField(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  function save() {
    setSaving(true);
    axios
      .patch(`${serverUrl}/school/${schoolId}/pdf-header`, settings, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then(() => {
        message.success("PDF header settings updated");
        queryClient.invalidateQueries(["school", schoolId]);
      })
      .catch((error) => {
        message.error(error?.response?.data?.message || error.message);
      })
      .finally(() => setSaving(false));
  }

  const schoolName = school?.name || "SCHOOL NAME";
  const schoolAddress = [school?.address, school?.city, school?.state, school?.pincode]
    .filter(Boolean)
    .join(", ");
  // Falls back to the logged-in admin's own account contact info, same as
  // the fee receipt PDF does server-side, since the Schools profile itself
  // often has no phone/email filled in.
  const schoolPhone =
    school?.phone || school?.owner?.phone || school?.head_master?.phone || user?.phone;
  const schoolEmail =
    school?.email || school?.owner?.email || school?.head_master?.email || user?.email;
  const contactLine = [
    settings.show_phone && schoolPhone ? `Phone: ${schoolPhone}` : null,
    settings.show_email && schoolEmail ? `Email: ${schoolEmail}` : null,
  ]
    .filter(Boolean)
    .join("   |   ");

  return (
    <div className="pt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="text-2xl text-orange-500 font-semibold pb-2">
          PDF Header
        </div>
        <div className="text-sm text-gray-500 pb-6">
          Controls the branding header shown at the top of generated fee
          receipts and progress card PDFs.
        </div>

        <div className="flex" style={{ gap: 32, flexWrap: "wrap" }}>
          <div style={{ minWidth: 260 }}>
            <div className="text-sm font-semibold pb-3" style={{ color: "#374151" }}>
              School Details
            </div>
            <div className="flex flex-col" style={{ gap: 10 }}>
              <Checkbox
                checked={settings.show_logo}
                onChange={(e) => updateField("show_logo", e.target.checked)}
              >
                Logo
              </Checkbox>
              <Checkbox
                checked={settings.show_school_name}
                onChange={(e) => updateField("show_school_name", e.target.checked)}
              >
                School Name
              </Checkbox>
              <Checkbox
                checked={settings.show_address}
                onChange={(e) => updateField("show_address", e.target.checked)}
              >
                Address
              </Checkbox>
              <Checkbox
                checked={settings.show_phone}
                onChange={(e) => updateField("show_phone", e.target.checked)}
              >
                Phone
              </Checkbox>
              <Checkbox
                checked={settings.show_email}
                onChange={(e) => updateField("show_email", e.target.checked)}
              >
                Email
              </Checkbox>
              <Checkbox
                checked={settings.show_website}
                onChange={(e) => updateField("show_website", e.target.checked)}
              >
                Website
              </Checkbox>
            </div>

            <div className="text-sm font-semibold pt-6 pb-2" style={{ color: "#374151" }}>
              Logo Size
            </div>
            <InputNumber
              value={settings.logo_size}
              onChange={(value) => updateField("logo_size", value ?? DEFAULT_SETTINGS.logo_size)}
              min={30}
              max={140}
              addonAfter="px"
              style={{ width: 140 }}
            />

            <div className="text-sm font-semibold pt-6 pb-2" style={{ color: "#374151" }}>
              Accent Color
            </div>
            <div className="text-xs text-gray-400 pb-2">
              Used for the school name and divider line.
            </div>
            <ColorPicker
              value={settings.accent_color}
              onChange={(color) => updateField("accent_color", color.toHexString())}
              showText
            />

            <div className="text-sm font-semibold pt-6 pb-2" style={{ color: "#374151" }}>
              Text Color
            </div>
            <div className="text-xs text-gray-400 pb-2">
              Used for the address, phone/email, and website lines.
            </div>
            <ColorPicker
              value={settings.text_color}
              onChange={(color) => updateField("text_color", color.toHexString())}
              showText
            />

            <div className="text-sm font-semibold pt-6 pb-2" style={{ color: "#374151" }}>
              School Name Font Size
            </div>
            <InputNumber
              value={settings.name_font_size}
              onChange={(value) => updateField("name_font_size", value ?? DEFAULT_SETTINGS.name_font_size)}
              min={10}
              max={32}
              addonAfter="pt"
              style={{ width: 140 }}
            />

            <div className="text-sm font-semibold pt-6 pb-2" style={{ color: "#374151" }}>
              Sub Header Font Size
            </div>
            <div className="text-xs text-gray-400 pb-2">
              Used for the address, phone/email, and website lines.
            </div>
            <InputNumber
              value={settings.text_font_size}
              onChange={(value) => updateField("text_font_size", value ?? DEFAULT_SETTINGS.text_font_size)}
              min={6}
              max={16}
              addonAfter="pt"
              style={{ width: 140 }}
            />

            <div className="flex justify-end pt-8">
              <Button type="primary" size="large" loading={saving} onClick={save}>
                Save Changes
              </Button>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 280 }}>
            <div className="text-sm font-semibold pb-3" style={{ color: "#374151" }}>
              Preview
            </div>
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 24,
                // Inner content box is exactly 505px (border-box, so padding
                // doesn't eat into it) — the same raw number as the fee
                // receipt PDF's content width in points (505.28pt, the
                // narrower of the two PDFs). Font/logo sizes here reuse the
                // PDF's raw point numbers as px, so matching this container
                // to the same raw number keeps the font-size-to-width ratio
                // identical, meaning text wraps here exactly like it will in
                // the real PDF.
                width: 505 + 48,
                maxWidth: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ width: settings.logo_size + 14, flexShrink: 0 }}>
                  {settings.show_logo && school?.logo && (
                    <img
                      src={`${cloudFrontUrl}/common/${school.logo}`}
                      alt={schoolName}
                      style={{
                        width: settings.logo_size,
                        height: settings.logo_size,
                        borderRadius: 6,
                        objectFit: "contain",
                      }}
                    />
                  )}
                  {settings.show_logo && !school?.logo && (
                    <div
                      style={{
                        width: settings.logo_size,
                        height: settings.logo_size,
                        borderRadius: 6,
                        background: settings.accent_color,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: Math.round(settings.logo_size * 0.4),
                        fontWeight: "bold",
                      }}
                    >
                      {schoolName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div style={{ flex: 1, textAlign: "center" }}>
                  {settings.show_school_name && (
                    <div style={{ fontSize: settings.name_font_size, fontWeight: "bold", color: settings.accent_color }}>
                      {schoolName.toUpperCase()}
                    </div>
                  )}
                  {settings.show_address && schoolAddress && (
                    <div style={{ fontSize: settings.text_font_size, color: settings.text_color, marginTop: 4 }}>
                      {schoolAddress}
                    </div>
                  )}
                  {contactLine && (
                    <div style={{ fontSize: settings.text_font_size, color: settings.text_color, marginTop: 2 }}>
                      {contactLine}
                    </div>
                  )}
                  {settings.show_website && school?.website && (
                    <div style={{ fontSize: settings.text_font_size, color: settings.text_color, marginTop: 2 }}>
                      {school.website}
                    </div>
                  )}
                </div>

                <div style={{ width: settings.logo_size + 14, flexShrink: 0 }} />
              </div>
              <div
                style={{
                  borderTop: `1.5px solid ${settings.accent_color}`,
                  marginTop: 10,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfHeaderSettings;
